// Top informational banner — full-width, above the main navigation.
//
// Three items: Contact/Reservations link · email mailto · background-music
// controls. Designed to free up space in the primary navbar while still
// keeping these high-intent actions a single tap away on both desktop and
// mobile.

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Music2, Play, Pause, Volume2, VolumeX, ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useMusicPlayer } from "@/contexts/MusicPlayerContext";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Tone: bone background + deep ink text with terracotta hover, matches
// the existing palette without competing with the main navbar.
const BAR_CLS =
  "fixed inset-x-0 top-0 z-[55] h-9 bg-[#2C2621] text-[#FDFBF7] " +
  "border-b border-[#FDFBF7]/10";

const ITEM_BTN =
  "inline-flex items-center gap-2 px-2 md:px-3 h-9 text-[10px] md:text-[11px] " +
  "tracking-[0.18em] uppercase whitespace-nowrap text-[#FDFBF7]/80 hover:text-[#D4A373] " +
  "transition-colors";

const TopBanner = () => {
  const { lang } = useLanguage();
  const { isPlaying, isMuted, volume, error, togglePlay, toggleMute, setVolume } = useMusicPlayer();

  const [musicOpen, setMusicOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Click-outside to close the music popover.
  useEffect(() => {
    if (!musicOpen) return undefined;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setMusicOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [musicOpen]);

  const contactLabel = pick(
    { en: "Contact / Bookings", fr: "Contact / Réservations", es: "Contacto / Reservas" },
    lang
  );
  const musicLabel = pick({ en: "Music", fr: "Musique", es: "Música" }, lang);
  const playLabel = isPlaying ? pick({ en: "Pause", fr: "Pause", es: "Pausar" }, lang) : pick({ en: "Play", fr: "Lire", es: "Reproducir" }, lang);
  const muteLabel = isMuted ? pick({ en: "Unmute", fr: "Activer le son", es: "Activar sonido" }, lang) : pick({ en: "Mute", fr: "Couper le son", es: "Silenciar" }, lang);
  const volumeLabel = pick({ en: "Volume", fr: "Volume", es: "Volumen" }, lang);

  return (
    <div className={BAR_CLS} data-testid="top-banner" role="region" aria-label="Top utility bar">
      <div className="max-w-7xl mx-auto h-full px-4 md:px-12 flex items-center justify-end flex-nowrap">
        {/* Arabic welcome — مرحبًا بكم (non-interactive greeting).
            Hidden on mobile (< sm) to free up space; full footer carries
            the greeting on all viewports instead. */}
        <span
          lang="ar"
          dir="rtl"
          className="hidden sm:inline-flex items-center px-2 md:px-3 h-9 text-[12px] md:text-[13px] whitespace-nowrap text-[#D4A373]/95 select-none"
          data-testid="top-banner-arabic-welcome"
          aria-label="Welcome in Arabic"
        >
          مرحبًا بكم
        </span>

        <span
          className="hidden sm:inline shrink-0 text-[#FDFBF7]/30 select-none"
          aria-hidden="true"
          data-testid="top-banner-divider-0"
        >
          |
        </span>

        {/* Contact link */}
        <Link
          to="/contact"
          className={ITEM_BTN}
          data-testid="top-banner-contact"
        >
          <Phone className="w-3.5 h-3.5 text-[#D4A373]" strokeWidth={1.6} aria-hidden="true" />
          <span>{contactLabel}</span>
        </Link>

        <span
          className="shrink-0 text-[#FDFBF7]/30 select-none"
          aria-hidden="true"
          data-testid="top-banner-divider-1"
        >
          |
        </span>

        {/* Email */}
        <a
          href="mailto:xaluca@xaluca.com"
          className={ITEM_BTN}
          data-testid="top-banner-email"
          aria-label="Send us an email"
        >
          <Mail className="w-3.5 h-3.5 text-[#D4A373]" strokeWidth={1.6} aria-hidden="true" />
          <span className="hidden sm:inline">xaluca@xaluca.com</span>
          <span className="sm:hidden">Email</span>
        </a>

        <span
          className="shrink-0 text-[#FDFBF7]/30 select-none"
          aria-hidden="true"
          data-testid="top-banner-divider-2"
        >
          |
        </span>

        {/* Music controls (button + popover) */}
        <div className="relative" ref={wrapperRef}>
          <button
            type="button"
            onClick={() => setMusicOpen((v) => !v)}
            className={ITEM_BTN}
            aria-haspopup="dialog"
            aria-expanded={musicOpen}
            data-testid="top-banner-music"
            title={error ? "Background music unavailable" : musicLabel}
          >
            <Music2
              className={`w-3.5 h-3.5 transition-colors ${isPlaying ? "text-[#D4A373]" : "text-[#D4A373]/70"}`}
              strokeWidth={1.6}
              aria-hidden="true"
            />
            <span>{musicLabel}</span>
            <ChevronDown
              className={`w-3 h-3 transition-transform ${musicOpen ? "rotate-180" : ""}`}
              strokeWidth={1.8}
              aria-hidden="true"
            />
          </button>

          {/* Popover */}
          <div
            className={`absolute right-0 top-full mt-2 w-[260px] bg-[#FBF6EE] text-[#2C2621] shadow-[0_18px_40px_-12px_rgba(28,22,18,0.45)] border border-[#2C2621]/10 transition-all duration-200 ${
              musicOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"
            }`}
            role="dialog"
            aria-label="Background music controls"
            data-testid="top-banner-music-popover"
          >
            <div className="px-4 py-3 border-b border-[#2C2621]/10">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#C16542]">
                {musicLabel}
              </p>
              <p className="mt-1 text-xs text-[#5C5248] leading-relaxed">
                {pick(
                  {
                    en: "Ambient sound of southern Morocco. Plays in the background as you browse.",
                    fr: "Ambiance sonore du sud marocain. Continue à jouer pendant que vous naviguez.",
                    es: "Ambiente sonoro del sur de Marruecos. Sigue sonando mientras navega.",
                  },
                  lang
                )}
              </p>
            </div>

            {error ? (
              <div className="px-4 py-3 text-xs text-[#5C5248]" data-testid="top-banner-music-error">
                {pick(
                  {
                    en: "Background music is temporarily unavailable.",
                    fr: "La musique d'ambiance est momentanément indisponible.",
                    es: "La música de fondo no está disponible en este momento.",
                  },
                  lang
                )}
              </div>
            ) : (
              <div className="px-4 py-3 space-y-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={togglePlay}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2C2621] hover:bg-[#1A1513] text-[#FDFBF7] px-3 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
                    data-testid="top-banner-music-play"
                    aria-pressed={isPlaying}
                  >
                    {isPlaying ? <Pause className="w-3.5 h-3.5" strokeWidth={1.8} /> : <Play className="w-3.5 h-3.5" strokeWidth={1.8} />}
                    {playLabel}
                  </button>
                  <button
                    type="button"
                    onClick={toggleMute}
                    className={`inline-flex items-center justify-center w-10 h-10 border transition-colors ${
                      isMuted
                        ? "border-[#C16542] text-[#C16542] hover:bg-[#C16542] hover:text-[#FDFBF7]"
                        : "border-[#2C2621]/15 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542]"
                    }`}
                    data-testid="top-banner-music-mute"
                    aria-pressed={isMuted}
                    aria-label={muteLabel}
                    title={muteLabel}
                  >
                    {isMuted ? <VolumeX className="w-4 h-4" strokeWidth={1.8} /> : <Volume2 className="w-4 h-4" strokeWidth={1.8} />}
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <Volume2 className="w-3.5 h-3.5 text-[#5C5248]" strokeWidth={1.6} aria-hidden="true" />
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={isMuted ? 0 : volume}
                    onChange={(e) => setVolume(e.target.value)}
                    className="flex-1 accent-[#C16542] h-1 cursor-pointer"
                    aria-label={volumeLabel}
                    data-testid="top-banner-music-volume"
                  />
                  <span className="text-[10px] tabular-nums text-[#5C5248] w-8 text-right">
                    {Math.round((isMuted ? 0 : volume) * 100)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBanner;
